import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  ArrowLeft, Star, Wifi, Plug, Volume2, Car, Coffee, 
  Building2, MapPin, Clock, Globe, Phone, Heart, Share2,
  MessageSquare, Send, Users, BadgeCheck, Facebook, Twitter, Instagram, Linkedin, Link2, X,
  AlertTriangle, Loader2, UtensilsCrossed, Calendar, CheckCircle, ChevronLeft, ChevronRight,
  Sun, Wind, Leaf, Dog, Accessibility, Monitor, Printer, LogIn, Award, Mail
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import VenueMap from "../components/VenueMap";
import DirectionsMenu from "../components/DirectionsMenu";
import AdBanner from "../components/AdBanner";
import SEO, { VenueStructuredData, BreadcrumbStructuredData } from "../components/SEO";
import BookingLinks from "../components/BookingLinks";
import BestTimesCalendar from "../components/BestTimesCalendar";
import { PriceDisplay } from "../components/PriceRating";
import ContributorAuthModal from "../components/ContributorAuthModal";
import WiFiSpeedGauge from "../components/WiFiSpeedGauge";
import EzoicAd from "../components/EzoicAd";
import { initCovertWiFiMonitor, trackVenueVisit } from "../utils/covertWiFiMonitor";
import { getVenueImage } from "../utils/defaultImages";
import { trackPageView, trackAction, incrementPageCount } from "../utils/analytics";

// Google icon SVG component
const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const USER_ID = "guest-user";

const ERROR_TYPES = [
  { value: "wrong_address", label: "Wrong address or location" },
  { value: "closed", label: "Venue is permanently closed" },
  { value: "wrong_hours", label: "Wrong opening hours" },
  { value: "wrong_amenities", label: "Incorrect amenities listed" },
  { value: "wrong_contact", label: "Wrong contact information" },
  { value: "other", label: "Other issue" }
];

// Motion animation presets - extracted to prevent re-renders
const MOTION_FADE_IN = { opacity: 0, y: 10 };
const MOTION_FADE_IN_VISIBLE = { opacity: 1, y: 0 };
const MOTION_POPUP = { opacity: 0, scale: 0.95, y: -10 };
const MOTION_POPUP_VISIBLE = { opacity: 1, scale: 1, y: 0 };
const MOTION_MODAL = { opacity: 0, scale: 0.9 };
const MOTION_MODAL_VISIBLE = { opacity: 1, scale: 1 };
const MOTION_OVERLAY = { opacity: 0 };
const MOTION_OVERLAY_VISIBLE = { opacity: 1 };

export default function VenueDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [venue, setVenue] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({ user_name: "", rating: 5, comment: "", wifi_speed_mbps: null, wifi_upload_mbps: null, wifi_latency_ms: null });
  const [wifiTestingForReview, setWifiTestingForReview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportForm, setReportForm] = useState({ error_type: "", message: "", reporter_email: "" });
  const [reportSubmitting, setReportSubmitting] = useState(false);
  
  // Workspace booking state
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [bookingForm, setBookingForm] = useState({
    time_slot: "morning",
    spaces: 1,
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    notes: ""
  });
  const [bookingSubmitting, setBookingSubmitting] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  
  // Image gallery state
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showFullGallery, setShowFullGallery] = useState(false);
  const galleryRef = useRef(null);
  
  // Contributor auth modal state (shown after review submission)
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [pendingReviewId, setPendingReviewId] = useState(null);
  
  // Contributor session state for Google auth
  const [contributorSession, setContributorSession] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);

  // Check if user is already logged in as contributor
  useEffect(() => {
    const checkContributorSession = async () => {
      try {
        const sessionToken = localStorage.getItem("contributor_session_token") || sessionStorage.getItem("contributor_session_token");
        const contributorId = localStorage.getItem("contributor_id") || sessionStorage.getItem("contributor_id");
        const storedName = localStorage.getItem("contributor_name") || sessionStorage.getItem("contributor_name");
        const storedEmail = localStorage.getItem("contributor_email") || sessionStorage.getItem("contributor_email");
        
        if (sessionToken && contributorId) {
          try {
            const response = await axios.get(`${API}/contributors/auth/me`, {
              headers: { Authorization: `Bearer ${sessionToken}` }
            });
            const sessionData = {
              ...response.data,
              // Ensure we have a name - prioritize API response, then localStorage, then email
              display_name: response.data?.display_name || response.data?.name || storedName || storedEmail?.split('@')[0] || 'User'
            };
            setContributorSession(sessionData);
            // Pre-fill the name field
            setReviewForm(prev => ({ ...prev, user_name: sessionData.display_name }));
          } catch (apiErr) {
            // API call failed but we have local session data - use that
            if (storedName || storedEmail) {
              const displayName = storedName || storedEmail?.split('@')[0] || 'User';
              setContributorSession({ 
                id: contributorId, 
                email: storedEmail, 
                display_name: displayName,
                name: displayName
              });
              setReviewForm(prev => ({ ...prev, user_name: displayName }));
            } else {
              setContributorSession(null);
            }
          }
        } else if (storedName || storedEmail) {
          // No session token but we have stored identity
          const displayName = storedName || storedEmail?.split('@')[0] || 'User';
          setContributorSession({ 
            id: contributorId, 
            email: storedEmail, 
            display_name: displayName,
            name: displayName
          });
          setReviewForm(prev => ({ ...prev, user_name: displayName }));
        }
      } catch (err) {
        // Session invalid - ignore
        setContributorSession(null);
      } finally {
        setCheckingAuth(false);
      }
    };
    
    checkContributorSession();
  }, []);

  // Define fetch functions with useCallback to prevent stale closures
  const fetchVenue = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/venues/${id}`);
      setVenue(response.data);
    } catch (error) {
      console.error("Failed to fetch venue:", error);
      // Auto-report system fault for venue loading failures
      if (error.response?.status !== 404) {
        try {
          await axios.post(`${API}/system/report-fault`, {
            fault_type: "api_error",
            component: "VenueDetailPage",
            message: `Failed to load venue ${id}: ${error.message}`,
            url: window.location.href,
            user_agent: navigator.userAgent,
            additional_data: { 
              venue_id: id, 
              status: error.response?.status,
              error: error.toString()
            }
          });
        } catch (reportError) {
          console.error("Failed to report fault:", reportError);
        }
      }
      setVenue(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchReviews = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/venues/${id}/reviews`);
      setReviews(response.data);
    } catch (error) {
      console.debug("Reviews fetch skipped:", error.message);
      // Reviews are not critical to page load - continue without them
    }
  }, [id]);

  const checkFavorite = useCallback(async () => {
    const favorites = JSON.parse(localStorage.getItem("favorites") || "[]");
    setIsFavorite(favorites.includes(id));
  }, [id]);

  useEffect(() => {
    // Track visitor with enhanced analytics
    trackPageView(`venue/${id}`, { venue_id: id });
    trackVenueVisit(id);  // Track view in backend
    incrementPageCount();
    
    fetchVenue();
    fetchReviews();
    checkFavorite();
  }, [id, fetchVenue, fetchReviews, checkFavorite]); // Analytics functions are stable module imports
  
  // Run covert WiFi test when venue loads
  useEffect(() => {
    if (venue && venue.id) {
      initCovertWiFiMonitor(venue);
    }
  }, [venue]);

  const toggleFavorite = async () => {
    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/${USER_ID}/${id}`);
        toast.success("Removed from favorites");
      } else {
        await axios.post(`${API}/favorites`, { user_id: USER_ID, venue_id: id });
        toast.success("Added to favorites");
        trackAction("favorite");
      }
      setIsFavorite(!isFavorite);
    } catch (error) {
      toast.error("Failed to update favorites");
    }
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    
    // Get name from form or from contributor session
    const userName = reviewForm.user_name || contributorSession?.display_name || contributorSession?.name || contributorSession?.email?.split('@')[0];
    
    if (!userName || !reviewForm.comment) {
      toast.error("Please fill all fields");
      return;
    }
    setSubmitting(true);
    try {
      // Submit review and get the review ID
      const response = await axios.post(`${API}/venues/${id}/reviews`, {
        user_name: userName,
        rating: reviewForm.rating,
        comment: reviewForm.comment,
        wifi_speed_mbps: reviewForm.wifi_speed_mbps,
        wifi_upload_mbps: reviewForm.wifi_upload_mbps,
        wifi_latency_ms: reviewForm.wifi_latency_ms
      });
      const reviewId = response.data?.review_id || response.data?.id;
      
      // If WiFi speed was included, also update the venue's WiFi average
      if (reviewForm.wifi_speed_mbps) {
        try {
          await axios.post(`${API}/venues/${id}/wifi-speed`, {
            download_mbps: reviewForm.wifi_speed_mbps,
            upload_mbps: reviewForm.wifi_upload_mbps,
            latency_ms: reviewForm.wifi_latency_ms,
            device_type: /mobile|android|iphone/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
            time_of_day: getTimeOfDay()
          });
        } catch (wifiErr) {
          console.debug("WiFi data update skipped:", wifiErr.message);
          // Non-critical - review still submitted successfully
        }
      }
      
      // If user is signed in with Google, automatically link review and award points
      if (contributorSession?.id && reviewId) {
        try {
          const linkResponse = await axios.post(
            `${API}/contributors/link-review?review_id=${reviewId}&contributor_id=${contributorSession.id}`
          );
          toast.success(
            <div className="flex items-center gap-2">
              <Award className="w-5 h-5 text-yellow-500" />
              <span>Review linked! <strong>+10 points</strong> earned!</span>
            </div>,
            { duration: 4000 }
          );
        } catch (linkErr) {
          console.debug("Review link skipped:", linkErr.message);
          toast.success("Review submitted!");
        }
      } else {
        toast.success(reviewForm.wifi_speed_mbps 
          ? `Review submitted with WiFi speed: ${reviewForm.wifi_speed_mbps} Mbps!` 
          : "Review submitted!");
      }
      
      setShowReviewForm(false);
      setReviewForm({ 
        user_name: contributorSession?.display_name || "", 
        rating: 5, 
        comment: "", 
        wifi_speed_mbps: null, 
        wifi_upload_mbps: null, 
        wifi_latency_ms: null 
      });
      fetchReviews();
      fetchVenue();
      
      // Show auth modal to encourage registration ONLY if not logged in
      if (reviewId && !contributorSession) {
        setPendingReviewId(reviewId);
        setShowAuthModal(true);
      }
    } catch (error) {
      toast.error("Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  // Helper to get time of day
  const getTimeOfDay = () => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) return 'morning';
    if (hour >= 12 && hour < 17) return 'afternoon';
    if (hour >= 17 && hour < 21) return 'evening';
    return 'night';
  };

  // Handle Google Sign-In for reviews
  const handleGoogleSignIn = () => {
    const redirectUrl = window.location.origin + '/contributor/auth/callback';
    
    // Store current URL to return after auth
    sessionStorage.setItem('auth_return_url', window.location.pathname);
    
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Handle contributor logout
  const handleContributorLogout = () => {
    localStorage.removeItem("contributor_session_token");
    localStorage.removeItem("contributor_id");
    localStorage.removeItem("contributor_email");
    sessionStorage.removeItem("contributor_session_token");
    sessionStorage.removeItem("contributor_id");
    setContributorSession(null);
    setReviewForm(prev => ({ ...prev, user_name: "" }));
    toast.success("Signed out");
  };

  // Run WiFi speed test for review - accurate multi-round testing
  const runWifiTestForReview = async () => {
    setWifiTestingForReview(true);
    toast.info("Running M-Lab speed test... (20-30 seconds)");
    
    try {
      // Use M-Lab NDT7 for accurate testing (same as Google speed test)
      const locateResponse = await fetch('https://locate.measurementlab.net/v2/nearest/ndt/ndt7?client_name=strider');
      const locateData = await locateResponse.json();
      
      if (!locateData.results || locateData.results.length === 0) {
        throw new Error("Couldn't find M-Lab test servers");
      }
      
      const server = locateData.results[0];
      const wsUrl = `wss://${server.machine}/ndt/v7/download`;
      
      // Run NDT7 download test
      let downloadSpeed = 0;
      let lastProgress = 0;
      
      await new Promise((resolve, reject) => {
        const ws = new WebSocket(wsUrl, 'net.measurementlab.ndt.v7');
        const startTime = Date.now();
        let totalBytes = 0;
        const timeout = setTimeout(() => {
          ws.close();
          reject(new Error("Test timed out"));
        }, 45000);
        
        ws.binaryType = 'arraybuffer';
        
        ws.onopen = () => {
          console.log("M-Lab connection established");
        };
        
        ws.onmessage = (event) => {
          if (event.data instanceof ArrayBuffer) {
            totalBytes += event.data.byteLength;
            const elapsedMs = Date.now() - startTime;
            if (elapsedMs > 0) {
              downloadSpeed = (totalBytes * 8) / (elapsedMs / 1000) / 1000000;
              // Update progress every 500ms
              if (elapsedMs - lastProgress > 500) {
                lastProgress = elapsedMs;
                toast.info(`Testing... ${Math.round(downloadSpeed)} Mbps`, { id: 'wifi-test-progress' });
              }
            }
          } else {
            // JSON measurement message
            try {
              const measurement = JSON.parse(event.data);
              if (measurement.TCPInfo?.BytesReceived) {
                const elapsedMs = Date.now() - startTime;
                downloadSpeed = (measurement.TCPInfo.BytesReceived * 8) / (elapsedMs / 1000) / 1000000;
              }
            } catch (e) {
              // Ignore parse errors
            }
          }
        };
        
        ws.onerror = (error) => {
          clearTimeout(timeout);
          reject(error);
        };
        
        ws.onclose = () => {
          clearTimeout(timeout);
          resolve();
        };
      });
      
      // Run latency test
      const latencies = [];
      for (let i = 0; i < 5; i++) {
        try {
          const start = performance.now();
          await fetch(`https://${server.machine}/ndt/v7/download?ping=${Date.now()}`, { 
            method: 'HEAD',
            cache: 'no-store' 
          });
          latencies.push(performance.now() - start);
        } catch (e) {
          // Continue with remaining samples
        }
      }
      
      let latencyMs = 30;
      if (latencies.length > 0) {
        const sorted = latencies.sort((a, b) => a - b);
        const trimmed = sorted.length > 2 ? sorted.slice(1, -1) : sorted;
        latencyMs = Math.round(trimmed.reduce((a, b) => a + b, 0) / trimmed.length);
      }
      
      const downloadMbps = Math.round(downloadSpeed * 10) / 10;
      const uploadMbps = Math.round(downloadMbps * 0.3 * 10) / 10; // Estimate upload
      
      if (downloadMbps < 1) {
        toast.error("Speed test failed - please try again");
        return;
      }
      
      setReviewForm(prev => ({
        ...prev,
        wifi_speed_mbps: downloadMbps,
        wifi_upload_mbps: uploadMbps,
        wifi_latency_ms: latencyMs
      }));
      
      toast.success(`Speed test complete: ${downloadMbps} Mbps`, { id: 'wifi-test-progress' });
      
    } catch (err) {
      console.error("M-Lab test failed, trying fallback:", err);
      
      // Fallback to CDN-based test
      toast.info("Using backup speed test...");
      
      const testFiles = [
        { url: 'https://cdn.jsdelivr.net/npm/three@0.160.0/build/three.min.js', size: 650000 },
        { url: 'https://cdn.jsdelivr.net/npm/chart.js@4.4.1/dist/chart.umd.min.js', size: 200000 },
      ];
      
      let totalBytes = 0;
      let totalTimeMs = 0;
      
      for (let round = 0; round < 3; round++) {
        for (const file of testFiles) {
          try {
            const url = file.url + '?t=' + Date.now() + '_' + Math.random();
            const startTime = performance.now();
            const response = await fetch(url, { cache: 'no-store', mode: 'cors' });
            const data = await response.arrayBuffer();
            const endTime = performance.now();
            
            totalBytes += data.byteLength;
            totalTimeMs += (endTime - startTime);
          } catch (err) {
            // Continue
          }
        }
      }
      
      if (totalBytes === 0) {
        toast.error("Speed test failed - couldn't reach test servers");
        return;
      }
      
      const downloadMbps = Math.round((totalBytes * 8 / (totalTimeMs / 1000)) / 1000000 * 10) / 10;
      const uploadMbps = Math.round(downloadMbps * 0.3 * 10) / 10;
      
      setReviewForm(prev => ({
        ...prev,
        wifi_speed_mbps: downloadMbps,
        wifi_upload_mbps: uploadMbps,
        wifi_latency_ms: 30
      }));
      
      toast.success(`Speed test complete: ${downloadMbps} Mbps`);
    } finally {
      setWifiTestingForReview(false);
    }
  };

  const handleShare = async (platform) => {
    const url = window.location.href;
    const text = `Check out ${venue?.name} on WorkAway - a great place to work remotely!`;
    const title = venue?.name;
    
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`,
    };
    
    if (platform === 'copy') {
      navigator.clipboard.writeText(url);
      toast.success("Link copied!");
      setShowShareMenu(false);
    } else if (platform === 'native') {
      try {
        await navigator.share({ title, text, url });
      } catch {
        setShowShareMenu(true);
      }
    } else if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
      setShowShareMenu(false);
    }
  };

  const handleReportError = async (e) => {
    e.preventDefault();
    if (!reportForm.error_type || !reportForm.message.trim()) {
      toast.error("Please select an error type and describe the issue");
      return;
    }
    
    setReportSubmitting(true);
    try {
      await axios.post(`${API}/venues/${id}/report-error`, {
        venue_id: id,
        venue_name: venue?.name,
        error_type: reportForm.error_type,
        message: reportForm.message.trim(),
        reporter_email: reportForm.reporter_email.trim() || null
      });
      toast.success("Thank you! Your report has been submitted for review.");
      setShowReportModal(false);
      setReportForm({ error_type: "", message: "", reporter_email: "" });
    } catch (error) {
      toast.error("Failed to submit report. Please try again.");
    } finally {
      setReportSubmitting(false);
    }
  };

  // Workspace booking functions
  const fetchAvailability = useCallback(async (date) => {
    if (!venue?.workspace_booking?.enabled) return;
    try {
      const response = await axios.get(`${API}/venues/${id}/availability?date=${date}`);
      setAvailability(response.data);
    } catch (error) {
      console.error("Failed to fetch availability:", error);
    }
  }, [venue?.workspace_booking?.enabled, id]);

  useEffect(() => {
    if (venue?.workspace_booking?.enabled) {
      fetchAvailability(selectedDate);
    }
  }, [venue?.workspace_booking?.enabled, selectedDate, fetchAvailability]);

  const handleBookWorkspace = async (e) => {
    e.preventDefault();
    if (!bookingForm.customer_name.trim() || !bookingForm.customer_email.trim()) {
      toast.error("Please enter your name and email");
      return;
    }
    
    setBookingSubmitting(true);
    try {
      await axios.post(`${API}/venues/${id}/book`, {
        date: selectedDate,
        time_slot: bookingForm.time_slot,
        spaces: bookingForm.spaces,
        customer_name: bookingForm.customer_name.trim(),
        customer_email: bookingForm.customer_email.trim(),
        customer_phone: bookingForm.customer_phone.trim() || null,
        notes: bookingForm.notes.trim() || null
      });
      setBookingSuccess(true);
      fetchAvailability(selectedDate);
      toast.success("Workspace booked successfully!");
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : "Failed to book workspace");
    } finally {
      setBookingSubmitting(false);
    }
  };

  const workspaceEnabled = venue?.is_premium && venue?.workspace_booking?.enabled;
  const totalSpaces = venue?.workspace_booking?.total_spaces || 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-surface">
        <Header />
        <div className="pt-20 px-4 max-w-6xl mx-auto">
          <div className="h-80 bg-gray-100 rounded-xl animate-pulse" />
        </div>
      </div>
    );
  }

  if (!venue) {
    return (
      <div className="min-h-screen bg-surface">
        <Header />
        <SEO 
          title="Venue Not Found"
          description="The workspace you're looking for doesn't exist or has been removed."
        />
        <div className="pt-24 pb-12 px-4">
          <div className="max-w-md mx-auto text-center">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Building2 className="w-10 h-10 text-gray-400" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-3">Venue Not Found</h1>
            <p className="text-gray-600 mb-8">
              The workspace you&apos;re looking for doesn&apos;t exist or may have been removed. 
              It could have closed down or the link might be incorrect.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button 
                onClick={() => navigate(-1)} 
                className="px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
              >
                <ArrowLeft className="w-5 h-5" />
                Go Back
              </button>
              <button 
                onClick={() => navigate('/browse')} 
                className="px-6 py-3 bg-brand text-white font-medium rounded-xl hover:bg-brand-dark transition-colors flex items-center justify-center gap-2"
              >
                <MapPin className="w-5 h-5" />
                Browse Workspaces
              </button>
            </div>
            <div className="mt-8 p-4 bg-amber-50 rounded-xl border border-amber-200">
              <p className="text-sm text-amber-800">
                <strong>Know a great workspace?</strong> Help the community by{' '}
                <button 
                  onClick={() => navigate('/add-location')}
                  className="text-brand hover:underline font-medium"
                >
                  adding it to Strider
                </button>
              </p>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Generate SEO-friendly venue type name
  const venueTypeName = venue ? {
    cafe: "Cafe",
    pub: "Pub",
    hotel: "Hotel",
    library: "Library",
    hub: "Coworking Hub",
    other: "Workspace"
  }[venue.venue_type] || "Workspace" : "Workspace";

  // Check if venue has custom branding
  const hasCustomBranding = venue?.is_premium && venue?.branding?.primary_color;
  
  // Dynamic styles for branded venues
  const brandStyles = hasCustomBranding ? {
    primary: venue.branding.primary_color,
    secondary: venue.branding.secondary_color || venue.branding.primary_color,
    accent: venue.branding.accent_color || '#fbbf24',
    text: venue.branding.text_color || '#ffffff',
  } : null;

  return (
    <div 
      className="min-h-screen"
      style={hasCustomBranding ? {
        backgroundColor: brandStyles.primary,
      } : { backgroundColor: '#f8fafc' }}
    >
      {venue && (
        <>
          <SEO 
            title={`${venue.name} - ${venueTypeName} for Remote Work in ${venue.location?.city || 'UK'}`}
            description={`${venue.name} in ${venue.location?.city || 'UK'}: ${venue.description?.slice(0, 150) || 'A great workspace'}${venue.description?.length > 150 ? '...' : ''} WiFi: ${venue.amenities?.wifi_quality || 'Available'}, Power outlets: ${venue.amenities?.power_outlets || 'Available'}. Rating: ${venue.rating || 'N/A'}/10.`}
            keywords={`${venue.name || 'workspace'}, ${venue.location?.city || 'UK'} coworking, ${venueTypeName?.toLowerCase() || 'workspace'} with WiFi ${venue.location?.city || ''}, remote work ${venue.location?.city || 'UK'}, laptop friendly ${venue.location?.city || ''}`}
            canonical={`/venue/${venue.id}`}
            type="place"
            image={venue.images?.[0]}
          />
          <VenueStructuredData venue={venue} />
          <BreadcrumbStructuredData items={[
            { name: "Home", path: "/" },
            { name: "Browse", path: "/browse" },
            { name: venue.location?.city, path: `/browse?city=${venue.location?.city}` },
            { name: venue.name, path: `/venue/${venue.id}` }
          ]} />
        </>
      )}
      <Header />
      
      {/* Premium Branding Banner */}
      {venue.is_premium && venue.branding?.primary_color && (
        <div 
          className="pt-16 pb-6"
          style={{
            background: `linear-gradient(135deg, ${venue.branding.primary_color}, ${venue.branding.secondary_color || venue.branding.primary_color})`
          }}
        >
          <div className="px-4 md:px-8 max-w-6xl mx-auto">
            <button 
              onClick={() => navigate(-1)} 
              className="flex items-center gap-2 text-sm mb-4 opacity-80 hover:opacity-100 transition-opacity" 
              style={{ color: venue.branding.text_color || '#ffffff' }}
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <div className="flex items-center gap-4">
              {venue.custom_logo_url ? (
                <img 
                  src={venue.custom_logo_url} 
                  alt={venue.name} 
                  className="w-16 h-16 rounded-xl object-contain bg-white/10 p-2"
                />
              ) : (
                <div 
                  className="w-16 h-16 rounded-xl flex items-center justify-center text-2xl font-bold"
                  style={{ 
                    backgroundColor: venue.branding.accent_color || '#ffffff20',
                    color: venue.branding.text_color || '#ffffff'
                  }}
                >
                  {venue.name?.charAt(0)}
                </div>
              )}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span 
                    className="text-xs font-medium uppercase opacity-80"
                    style={{ color: venue.branding.text_color || '#ffffff' }}
                  >
                    {venue.venue_type.replace("_", " ")}
                  </span>
                  <span className="opacity-50" style={{ color: venue.branding.text_color || '#ffffff' }}>•</span>
                  <span 
                    className="text-xs opacity-80"
                    style={{ color: venue.branding.text_color || '#ffffff' }}
                  >
                    {venue.location.city}
                  </span>
                </div>
                <h1 
                  className="text-2xl font-bold"
                  style={{ color: venue.branding.text_color || '#ffffff' }}
                >
                  {venue.name}
                </h1>
                <div className="flex items-center gap-3 mt-2">
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4" style={{ color: venue.branding.accent_color || '#fbbf24' }} fill={venue.branding.accent_color || '#fbbf24'} />
                    <span className="font-medium" style={{ color: venue.branding.text_color || '#ffffff' }}>{venue.rating?.toFixed(1) || "New"}</span>
                    <span className="text-sm opacity-60" style={{ color: venue.branding.text_color || '#ffffff' }}>({venue.review_count || 0})</span>
                  </div>
                  {venue.is_premium && (
                    <span 
                      className="px-2 py-0.5 rounded text-xs font-medium"
                      style={{ 
                        backgroundColor: venue.branding.accent_color || '#ffffff20',
                        color: venue.branding.text_color || '#ffffff'
                      }}
                    >
                      Featured
                    </span>
                  )}
                  {venue.is_verified && (
                    <span 
                      className="flex items-center gap-1 text-xs"
                      style={{ color: venue.branding.text_color || '#ffffff' }}
                    >
                      <BadgeCheck className="w-3 h-3" /> Verified
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Main Content - White box in center for branded venues */}
      <div className={hasCustomBranding ? "px-4 md:px-6 pb-8" : ""}>
        <div 
          className={hasCustomBranding 
            ? "bg-white rounded-3xl shadow-xl max-w-6xl mx-auto -mt-4 relative z-10 overflow-hidden" 
            : ""
          }
        >
          <div className={venue.is_premium && venue.branding?.primary_color ? "pt-4" : "pt-16"}>
            {/* Back - only show if no branded header */}
            {!(venue.is_premium && venue.branding?.primary_color) && (
              <div className="px-4 md:px-8 py-3 max-w-6xl mx-auto">
                <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-gray-600 hover:text-brand" data-testid="back-button">
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
              </div>
            )}

        {/* Images - Uniform size with premium scrolling gallery */}
        <div className="px-4 md:px-8 max-w-6xl mx-auto mb-6">
          {venue.is_premium && venue.images?.length > 1 ? (
            /* Premium Gallery - Scrolling carousel with captions */
            <div className="relative">
              {/* Main Image */}
              <div className="relative aspect-[16/9] rounded-xl overflow-hidden bg-gray-100">
                <img 
                  src={venue.images[currentImageIndex] || getVenueImage(venue, 800)} 
                  alt={`${venue.name} - Photo ${currentImageIndex + 1}`}
                  className="w-full h-full object-cover"
                />
                {venue.is_premium && <div className="absolute top-3 left-3 badge-premium">Featured</div>}
                {venue.is_verified && <div className="absolute top-3 right-12 badge-verified"><BadgeCheck className="w-3 h-3" /> Verified</div>}
                
                {/* Image Caption (if available) */}
                {venue.image_captions?.[currentImageIndex] && (
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                    <p className="text-white text-sm">{venue.image_captions[currentImageIndex]}</p>
                  </div>
                )}
                
                {/* Navigation Arrows */}
                {venue.images.length > 1 && (
                  <>
                    <button
                      onClick={() => setCurrentImageIndex(prev => prev === 0 ? venue.images.length - 1 : prev - 1)}
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                      data-testid="gallery-prev"
                    >
                      <ChevronLeft className="w-5 h-5 text-gray-700" />
                    </button>
                    <button
                      onClick={() => setCurrentImageIndex(prev => prev === venue.images.length - 1 ? 0 : prev + 1)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                      data-testid="gallery-next"
                    >
                      <ChevronRight className="w-5 h-5 text-gray-700" />
                    </button>
                  </>
                )}
                
                {/* Image Counter */}
                <div className="absolute bottom-3 right-3 px-3 py-1.5 bg-black/60 text-white text-xs rounded-full">
                  {currentImageIndex + 1} / {venue.images.length}
                </div>
              </div>
              
              {/* Thumbnail Strip with WiFi Gauge */}
              <div className="flex items-center gap-4 mt-3">
                <div 
                  ref={galleryRef}
                  className="flex-1 flex gap-2 overflow-x-auto pb-2 scrollbar-hide"
                  style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                >
                  {venue.images.map((img, i) => (
                    <button
                      key={`thumb-${img}-${i}`}
                      onClick={() => setCurrentImageIndex(i)}
                      className={`flex-shrink-0 w-20 h-14 rounded-lg overflow-hidden transition-all ${
                        currentImageIndex === i 
                          ? 'ring-2 ring-brand ring-offset-2' 
                          : 'opacity-70 hover:opacity-100'
                      }`}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
                
                {/* WiFi Speed Gauge */}
                {venue.wifi_speed_mbps > 0 && (
                  <div className="flex-shrink-0 bg-white rounded-xl p-3 border border-gray-200 shadow-sm">
                    <WiFiSpeedGauge 
                      speedMbps={venue.wifi_speed_mbps}
                      uploadMbps={venue.wifi_upload_mbps}
                      latencyMs={venue.wifi_latency_ms}
                      size="md"
                      showLabel={true}
                      showDetails={true}
                    />
                    {venue.wifi_test_count > 1 && (
                      <p className="text-xs text-gray-400 text-center mt-1">
                        Avg of {venue.wifi_test_count} tests
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Standard Gallery - Uniform grid */
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="relative aspect-[4/3] rounded-xl overflow-hidden bg-gray-100">
                <img 
                  src={getVenueImage(venue, 800)} 
                  alt={venue.name} 
                  className="w-full h-full object-cover"
                />
                {venue.is_premium && <div className="absolute top-3 left-3 badge-premium">Featured</div>}
                {venue.is_verified && <div className="absolute top-3 right-3 badge-verified"><BadgeCheck className="w-3 h-3" /> Verified</div>}
              </div>
              {venue.images?.length > 1 && (
                <div className="hidden md:grid grid-rows-2 gap-3">
                  {venue.images.slice(1, 3).map((img, i) => (
                    <div key={`gallery-${img}-${i}`} className="relative aspect-[4/3] rounded-xl overflow-hidden bg-gray-100">
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* WiFi Speed Badge - Between images and content */}
        {venue.wifi_speed_mbps > 0 && (
          <div className="px-4 md:px-8 max-w-6xl mx-auto mb-4">
            <div className="flex items-center gap-3">
              <div 
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl shadow-sm border ${
                  venue.wifi_speed_mbps >= 100 
                    ? 'bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200' 
                    : venue.wifi_speed_mbps >= 50 
                      ? 'bg-gradient-to-r from-blue-50 to-cyan-50 border-blue-200'
                      : venue.wifi_speed_mbps >= 25
                        ? 'bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200'
                        : 'bg-gradient-to-r from-gray-50 to-slate-50 border-gray-200'
                }`}
              >
                <Wifi 
                  className={`w-5 h-5 ${
                    venue.wifi_speed_mbps >= 100 
                      ? 'text-emerald-600' 
                      : venue.wifi_speed_mbps >= 50 
                        ? 'text-blue-600'
                        : venue.wifi_speed_mbps >= 25
                          ? 'text-amber-600'
                          : 'text-gray-600'
                  }`} 
                />
                <div className="flex flex-col">
                  <span 
                    className={`text-lg font-bold leading-tight ${
                      venue.wifi_speed_mbps >= 100 
                        ? 'text-emerald-700' 
                        : venue.wifi_speed_mbps >= 50 
                          ? 'text-blue-700'
                          : venue.wifi_speed_mbps >= 25
                            ? 'text-amber-700'
                            : 'text-gray-700'
                    }`}
                  >
                    {venue.wifi_speed_mbps} Mbps
                  </span>
                  <span className="text-xs text-gray-500 leading-tight">
                    {venue.wifi_speed_mbps >= 100 ? 'Ultra Fast' : venue.wifi_speed_mbps >= 50 ? 'Fast' : venue.wifi_speed_mbps >= 25 ? 'Good' : 'Basic'}
                  </span>
                </div>
              </div>
              {venue.wifi_speed_tier && (
                <span className="text-xs text-gray-400">
                  Verified speed
                </span>
              )}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="px-4 md:px-8 py-6 max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left */}
            <div className="lg:col-span-2 space-y-6">
              {/* Header */}
              <motion.div initial={MOTION_FADE_IN} animate={MOTION_FADE_IN_VISIBLE} className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span 
                      className="text-xs font-medium uppercase"
                      style={hasCustomBranding ? { color: brandStyles.primary } : {}}
                    >
                      <span className={hasCustomBranding ? "" : "text-brand"}>{venue.venue_type.replace("_", " ")}</span>
                    </span>
                    <span className="text-xs text-gray-400">•</span>
                    <span className="text-xs text-gray-500">{venue.location.city}</span>
                  </div>
                  <h1 className="text-2xl font-semibold text-gray-900">{venue.name}</h1>
                  <div className="flex items-center gap-3 mt-2">
                    <div className="flex items-center gap-1">
                      <Star 
                        className="w-4 h-4 fill-current" 
                        style={hasCustomBranding ? { color: brandStyles.accent } : { color: '#f59e0b' }}
                      />
                      <span className="font-medium">{venue.rating.toFixed(1)}</span>
                      <span className="text-gray-400 text-sm">({venue.review_count})</span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={toggleFavorite} className={`p-2.5 rounded-lg border transition-all ${isFavorite ? "bg-red-50 border-red-200 text-red-500" : "bg-white border-gray-200 text-gray-500 hover:text-red-500"}`} data-testid="favorite-button">
                    <Heart className={`w-5 h-5 ${isFavorite ? "fill-current" : ""}`} />
                  </button>
                  <DirectionsMenu 
                    venue={venue} 
                    buttonClassName="p-2.5 rounded-lg bg-teal-600 hover:bg-teal-700 text-white transition-colors shadow-sm"
                    buttonStyle={hasCustomBranding ? { backgroundColor: brandStyles.primary } : {}}
                  />
                  <div className="relative">
                    <button 
                      onClick={() => handleShare('native')} 
                      className={`p-2.5 rounded-lg bg-white border border-gray-200 text-gray-500 transition-colors`}
                      style={hasCustomBranding ? { ':hover': { color: brandStyles.primary } } : {}}
                      data-testid="share-button"
                    >
                      <Share2 className="w-5 h-5" />
                    </button>
                    
                    {/* Share Menu */}
                    <AnimatePresence>
                      {showShareMenu && (
                        <>
                          <div className="fixed inset-0 z-40 bg-black/20 md:bg-transparent" onClick={() => setShowShareMenu(false)} />
                          {/* Mobile: Centered bottom sheet style, Desktop: Dropdown */}
                          <motion.div
                            initial={MOTION_POPUP}
                            animate={MOTION_POPUP_VISIBLE}
                            exit={MOTION_POPUP}
                            className="fixed md:absolute left-4 right-4 bottom-4 md:left-auto md:right-0 md:bottom-auto md:top-12 z-50 bg-white rounded-xl shadow-xl border border-gray-200 p-2 md:min-w-[180px] md:w-auto"
                          >
                            <p className="text-xs font-medium text-gray-500 px-3 py-2">Share on</p>
                            <button onClick={() => handleShare('facebook')} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 text-sm text-gray-700" data-testid="share-facebook">
                              <Facebook className="w-4 h-4 text-blue-600" /> Facebook
                            </button>
                            <button onClick={() => handleShare('twitter')} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 text-sm text-gray-700" data-testid="share-twitter">
                              <Twitter className="w-4 h-4 text-sky-500" /> Twitter
                            </button>
                            <button onClick={() => handleShare('linkedin')} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 text-sm text-gray-700" data-testid="share-linkedin">
                              <Linkedin className="w-4 h-4 text-blue-700" /> LinkedIn
                            </button>
                            <button onClick={() => handleShare('whatsapp')} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 text-sm text-gray-700" data-testid="share-whatsapp">
                              <MessageSquare className="w-4 h-4 text-green-600" /> WhatsApp
                            </button>
                            <div className="border-t border-gray-100 my-1" />
                            <button onClick={() => handleShare('copy')} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 text-sm text-gray-700" data-testid="share-copy">
                              <Link2 className="w-4 h-4 text-gray-500" /> Copy link
                            </button>
                          </motion.div>
                        </>
                      )}
                    </AnimatePresence>
                  </div>
                  <button 
                    onClick={() => setShowReportModal(true)} 
                    className="p-2.5 rounded-lg bg-white border border-gray-200 text-gray-500 hover:text-orange-500 hover:border-orange-200" 
                    data-testid="report-error-button"
                    title="Report an error"
                  >
                    <AlertTriangle className="w-5 h-5" />
                  </button>
                </div>
              </motion.div>

              {/* About */}
              <motion.div initial={MOTION_FADE_IN} animate={MOTION_FADE_IN_VISIBLE} transition={{ delay: 0.1 }} className="bg-white rounded-xl p-5 border border-gray-100">
                <h2 className="font-semibold text-gray-900 mb-3">About</h2>
                <p className="text-sm text-gray-600 leading-relaxed">{venue.description}</p>
                {venue.is_premium && venue.premium_features?.special_offer && (
                  <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-sm text-amber-700 font-medium">Offer: {venue.premium_features.special_offer}</p>
                  </div>
                )}
              </motion.div>

              {/* Amenities */}
              <motion.div initial={MOTION_FADE_IN} animate={MOTION_FADE_IN_VISIBLE} transition={{ delay: 0.2 }} className="bg-white rounded-xl p-5 border border-gray-100">
                <h2 className="font-semibold text-gray-900 mb-4">Amenities</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {[
                    { icon: Wifi, label: "WiFi", value: venue.amenities.wifi_quality },
                    { icon: Plug, label: "Outlets", value: `${venue.amenities.power_outlets}+` },
                    { icon: Volume2, label: "Noise", value: venue.amenities.noise_level },
                    venue.amenities.has_meeting_rooms && { icon: Users, label: "Meeting Rooms", value: "Yes" },
                    venue.amenities.has_parking && { icon: Car, label: "Parking", value: "Yes" },
                    venue.amenities.has_food && { icon: Coffee, label: "Food & Drink", value: "Yes" },
                  ].filter(Boolean).map((item, i) => (
                    <div 
                      key={`amenity-${item.label}-${i}`} 
                      className="flex items-center gap-3 p-3 rounded-lg"
                      style={hasCustomBranding ? { backgroundColor: `${brandStyles.primary}10` } : { backgroundColor: '#f9fafb' }}
                    >
                      <item.icon 
                        className="w-5 h-5" 
                        style={hasCustomBranding ? { color: brandStyles.primary } : {}}
                      />
                      <div>
                        <p className="text-xs text-gray-500">{item.label}</p>
                        <p className="text-sm font-medium capitalize">{item.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Work-Friendly Features */}
                {(venue.amenities.long_stay_friendly || venue.amenities.good_for_calls || 
                  venue.amenities.natural_light || venue.amenities.has_ac || venue.amenities.is_spacious ||
                  venue.amenities.has_outdoor_seating || venue.amenities.pet_friendly || 
                  venue.amenities.wheelchair_accessible || venue.amenities.group_friendly || 
                  venue.amenities.has_standing_desks || venue.amenities.has_printing) && (
                  <>
                    <h3 className="font-medium text-gray-700 mt-5 mb-3">Work-Friendly Features</h3>
                    <div className="flex flex-wrap gap-2">
                      {venue.amenities.long_stay_friendly && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-sm font-medium">
                          <Clock className="w-4 h-4" />
                          Long Stays Welcome
                        </span>
                      )}
                      {venue.amenities.good_for_calls && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-50 text-green-700 rounded-full text-sm font-medium">
                          <Phone className="w-4 h-4" />
                          Good for Calls
                        </span>
                      )}
                      {venue.amenities.natural_light && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-yellow-50 text-yellow-700 rounded-full text-sm font-medium">
                          <Sun className="w-4 h-4" />
                          Natural Light
                        </span>
                      )}
                      {venue.amenities.has_ac && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-cyan-50 text-cyan-700 rounded-full text-sm font-medium">
                          <Wind className="w-4 h-4" />
                          Air Conditioning
                        </span>
                      )}
                      {venue.amenities.is_spacious && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 text-purple-700 rounded-full text-sm font-medium">
                          <Building2 className="w-4 h-4" />
                          Spacious
                        </span>
                      )}
                      {venue.amenities.has_outdoor_seating && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-full text-sm font-medium">
                          <Leaf className="w-4 h-4" />
                          Outdoor Seating
                        </span>
                      )}
                      {venue.amenities.pet_friendly && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 text-orange-700 rounded-full text-sm font-medium">
                          <Dog className="w-4 h-4" />
                          Pet Friendly
                        </span>
                      )}
                      {venue.amenities.wheelchair_accessible && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-full text-sm font-medium">
                          <Accessibility className="w-4 h-4" />
                          Wheelchair Accessible
                        </span>
                      )}
                      {venue.amenities.group_friendly && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-pink-50 text-pink-700 rounded-full text-sm font-medium">
                          <Users className="w-4 h-4" />
                          Group Friendly
                        </span>
                      )}
                      {venue.amenities.has_standing_desks && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
                          <Monitor className="w-4 h-4" />
                          Standing Desks
                        </span>
                      )}
                      {venue.amenities.has_printing && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-violet-50 text-violet-700 rounded-full text-sm font-medium">
                          <Printer className="w-4 h-4" />
                          Printing
                          {(venue.amenities.printing_cost_bw || venue.amenities.printing_cost_color) && (
                            <span className="ml-1 text-xs opacity-75">
                              (£{venue.amenities.printing_cost_bw || '?'}/{venue.amenities.printing_cost_color ? `£${venue.amenities.printing_cost_color}` : '?'})
                            </span>
                          )}
                        </span>
                      )}
                    </div>
                  </>
                )}
              </motion.div>

              {/* Workspace Booking (Premium Feature) */}
              {workspaceEnabled && (
                <motion.div 
                  initial={{ opacity: 0, y: 10 }} 
                  animate={{ opacity: 1, y: 0 }} 
                  transition={{ delay: 0.25 }} 
                  className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-100"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-blue-600" />
                        Book a Workspace
                      </h2>
                      <p className="text-sm text-gray-500 mt-1">Reserve your spot in advance</p>
                    </div>
                    {availability && (
                      <div className="text-right">
                        <p className="text-2xl font-bold text-blue-600">
                          {Math.min(
                            availability.slots?.morning?.available_spaces || 0,
                            availability.slots?.afternoon?.available_spaces || 0
                          )}
                        </p>
                        <p className="text-xs text-gray-500">spaces available today</p>
                      </div>
                    )}
                  </div>

                  <div className="space-y-4">
                    {/* Date Picker */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Select Date</label>
                      <input
                        type="date"
                        value={selectedDate}
                        min={new Date().toISOString().split('T')[0]}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                        data-testid="booking-date"
                      />
                    </div>

                    {/* Time Slots */}
                    {availability && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Available Slots</label>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                          {Object.entries(availability.slots || {}).map(([slotId, slot]) => (
                            <button
                              key={slotId}
                              type="button"
                              disabled={!slot.is_available}
                              onClick={() => setBookingForm({ ...bookingForm, time_slot: slotId })}
                              className={`p-3 rounded-lg border text-left transition-all ${
                                bookingForm.time_slot === slotId
                                  ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                                  : slot.is_available
                                    ? 'border-gray-200 bg-white hover:border-blue-300'
                                    : 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'
                              }`}
                              data-testid={`slot-${slotId}`}
                            >
                              <p className="font-medium text-sm">{slot.label}</p>
                              <p className={`text-xs ${slot.is_available ? 'text-green-600' : 'text-red-500'}`}>
                                {slot.available_spaces} of {slot.total_spaces} available
                              </p>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Book Button */}
                    <button
                      onClick={() => setShowBookingModal(true)}
                      disabled={!availability?.slots?.[bookingForm.time_slot]?.is_available}
                      className="w-full py-3 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      style={hasCustomBranding ? { backgroundColor: brandStyles.primary } : { backgroundColor: '#2563eb' }}
                      data-testid="open-booking-modal"
                    >
                      <Calendar className="w-4 h-4" />
                      Book This Space
                    </button>
                  </div>
                </motion.div>
              )}

              {/* Reviews */}
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-white rounded-xl p-5 border border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-semibold text-gray-900">Reviews ({reviews.length})</h2>
                  <button 
                    onClick={() => setShowReviewForm(!showReviewForm)} 
                    className="text-sm font-medium"
                    style={hasCustomBranding ? { color: brandStyles.primary } : {}}
                    data-testid="write-review-btn"
                  >
                    <span className={hasCustomBranding ? "" : "text-brand hover:text-brand-dark"}>Write Review</span>
                  </button>
                </div>

                {showReviewForm && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-5">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                      {/* Review Form - 2 columns */}
                      <form onSubmit={handleReviewSubmit} className="lg:col-span-2 p-4 bg-gray-50 rounded-lg">
                    
                    {/* Sign-In Options Banner */}
                    {!contributorSession ? (
                      <div className="mb-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
                        <div className="flex items-center gap-2 mb-3">
                          <Award className="w-5 h-5 text-yellow-500" />
                          <div>
                            <p className="text-sm font-medium text-gray-800">Sign in to earn points!</p>
                            <p className="text-xs text-gray-500">Get +10 points for each review</p>
                          </div>
                        </div>
                        
                        {/* Auth Options */}
                        <div className="flex flex-col sm:flex-row gap-2">
                          <button
                            type="button"
                            onClick={handleGoogleSignIn}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors shadow-sm text-sm font-medium touch-target-sm"
                            data-testid="google-signin-review-btn"
                          >
                            <GoogleIcon />
                            <span>Google</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setPendingReviewId(null);
                              setShowAuthModal(true);
                            }}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-brand text-white rounded-lg hover:bg-brand-dark transition-colors shadow-sm text-sm font-medium touch-target-sm"
                            data-testid="email-signin-review-btn"
                          >
                            <Mail className="w-4 h-4" />
                            <span>Email</span>
                          </button>
                        </div>
                        <p className="text-xs text-gray-400 text-center mt-2">Or continue as guest below</p>
                      </div>
                    ) : (
                      <div className="mb-4 p-3 bg-green-50 rounded-lg border border-green-100">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-brand text-white flex items-center justify-center text-sm font-medium">
                              {contributorSession.display_name?.charAt(0)?.toUpperCase() || 'U'}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-800">{contributorSession.display_name}</p>
                              <p className="text-xs text-green-600 flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" />
                                Reviews will earn +10 points automatically
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={handleContributorLogout}
                            className="text-xs text-gray-400 hover:text-gray-600"
                          >
                            Sign out
                          </button>
                        </div>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                      <div>
                        <input 
                          type="text" 
                          value={reviewForm.user_name} 
                          onChange={(e) => setReviewForm({ ...reviewForm, user_name: e.target.value })} 
                          className={`w-full px-3 py-2 rounded-lg border text-sm ${contributorSession ? 'bg-gray-100 border-gray-100' : 'border-gray-200'}`}
                          placeholder="Your name" 
                          data-testid="review-name-input"
                          readOnly={!!contributorSession}
                        />
                        {contributorSession && (
                          <p className="text-xs text-gray-400 mt-1">Name linked to your account</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Your Rating (out of 10)</label>
                        <select value={reviewForm.rating} onChange={(e) => setReviewForm({ ...reviewForm, rating: parseInt(e.target.value) })} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" data-testid="review-rating-select">
                          {[10, 9, 8, 7, 6, 5, 4, 3, 2, 1].map(n => <option key={n} value={n}>{n}/10</option>)}
                        </select>
                      </div>
                    </div>
                    <textarea value={reviewForm.comment} onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm h-20 resize-none mb-3" placeholder="Your review..." data-testid="review-comment-input" />
                    
                    {/* WiFi Speed Test Section */}
                    <div className="mb-3 p-3 bg-white rounded-lg border border-gray-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Wifi className="w-4 h-4 text-teal-600" />
                          <span className="text-sm font-medium text-gray-700">Add WiFi Speed</span>
                          <span className="text-xs text-gray-400">(optional)</span>
                        </div>
                        {reviewForm.wifi_speed_mbps ? (
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              reviewForm.wifi_speed_mbps >= 100 ? 'bg-teal-100 text-teal-700' :
                              reviewForm.wifi_speed_mbps >= 50 ? 'bg-green-100 text-green-700' :
                              reviewForm.wifi_speed_mbps >= 25 ? 'bg-yellow-100 text-yellow-700' :
                              'bg-orange-100 text-orange-700'
                            }`}>
                              {reviewForm.wifi_speed_mbps} Mbps
                            </span>
                            <button
                              type="button"
                              onClick={() => setReviewForm(prev => ({ ...prev, wifi_speed_mbps: null, wifi_upload_mbps: null, wifi_latency_ms: null }))}
                              className="text-xs text-gray-400 hover:text-red-500"
                            >
                              Clear
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={runWifiTestForReview}
                            disabled={wifiTestingForReview}
                            className="px-3 py-1.5 bg-teal-600 hover:bg-teal-700 text-white text-xs font-medium rounded-lg disabled:opacity-50 flex items-center gap-1.5"
                          >
                            {wifiTestingForReview ? (
                              <>
                                <Loader2 className="w-3 h-3 animate-spin" />
                                Testing...
                              </>
                            ) : (
                              <>
                                <Wifi className="w-3 h-3" />
                                Test WiFi Now
                              </>
                            )}
                          </button>
                        )}
                      </div>
                      {reviewForm.wifi_speed_mbps && (
                        <div className="mt-2 flex gap-3 text-xs text-gray-500">
                          <span>↓ {reviewForm.wifi_speed_mbps} Mbps</span>
                          <span>↑ {reviewForm.wifi_upload_mbps} Mbps</span>
                          <span>{reviewForm.wifi_latency_ms}ms latency</span>
                        </div>
                      )}
                    </div>
                    
                    <button 
                      type="submit" 
                      disabled={submitting} 
                      className="text-sm flex items-center gap-2 px-4 py-2 text-white rounded-lg disabled:opacity-50"
                      style={hasCustomBranding ? { backgroundColor: brandStyles.primary } : { backgroundColor: '#008B8B' }}
                      data-testid="submit-review-btn"
                    >
                      <Send className="w-4 h-4" /> {submitting ? "Submitting..." : "Submit"}
                    </button>
                  </form>
                      
                      {/* Review Guidelines - 1 column on right */}
                      <div className="p-4 bg-gradient-to-br from-teal-50 to-blue-50 rounded-lg border border-teal-100">
                        <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-teal-600" />
                          Review Guidelines
                        </h4>
                        
                        <div className="mb-4">
                          <p className="text-xs font-medium text-teal-700 uppercase tracking-wide mb-2">We Want To Know About</p>
                          <ul className="space-y-1.5 text-sm text-gray-700">
                            <li className="flex items-start gap-2">
                              <span className="text-teal-500 mt-0.5">✓</span>
                              <span>Comfort & seating quality</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-teal-500 mt-0.5">✓</span>
                              <span>Noise levels & atmosphere</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-teal-500 mt-0.5">✓</span>
                              <span>Power socket availability</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-teal-500 mt-0.5">✓</span>
                              <span>Staff friendliness</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-teal-500 mt-0.5">✓</span>
                              <span>Suitability for work</span>
                            </li>
                          </ul>
                        </div>
                        
                        <div>
                          <p className="text-xs font-medium text-red-600 uppercase tracking-wide mb-2">Please Avoid</p>
                          <ul className="space-y-1.5 text-sm text-gray-600">
                            <li className="flex items-start gap-2">
                              <span className="text-red-400 mt-0.5">✗</span>
                              <span>Food/drink quality reviews</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-red-400 mt-0.5">✗</span>
                              <span>Décor or aesthetic opinions</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-red-400 mt-0.5">✗</span>
                              <span>Non-work-related comments</span>
                            </li>
                          </ul>
                        </div>
                        
                        <p className="mt-4 text-xs text-gray-500 italic">
                          Focus on what matters for remote workers!
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}

                <div className="space-y-3">
                  {reviews.length === 0 ? (
                    <p className="text-gray-500 text-center py-6 text-sm">No reviews yet</p>
                  ) : reviews.map((review) => (
                    <div key={review.id} className="border-b border-gray-100 pb-3 last:border-0">
                      <div className="flex items-center gap-2 mb-1">
                        <div 
                          className="w-8 h-8 rounded-full text-white flex items-center justify-center text-sm font-medium"
                          style={hasCustomBranding ? { backgroundColor: brandStyles.primary } : { backgroundColor: '#008B8B' }}
                        >
                          {(review.user_name || 'A').charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-medium">{review.user_name || 'Anonymous'}</p>
                            {/* WiFi Speed Badge - shown if review includes WiFi test */}
                            {review.wifi_speed_mbps && (
                              <div className="flex items-center gap-1 px-2 py-0.5 bg-teal-50 text-teal-700 rounded-full text-xs">
                                <Wifi className="w-3 h-3" />
                                <span className="font-medium">{review.wifi_speed_mbps} Mbps</span>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <span 
                              className="font-medium"
                              style={hasCustomBranding ? { color: brandStyles.primary } : { color: '#008B8B' }}
                            >
                              {review.rating}/10
                            </span>
                            {/* Show WiFi details on hover/tap if available */}
                            {review.wifi_speed_mbps && (
                              <span className="text-gray-400" title={`↓${review.wifi_speed_mbps} ↑${review.wifi_upload_mbps || '?'} ${review.wifi_latency_ms ? `${review.wifi_latency_ms}ms` : ''}`}>
                                • WiFi tested
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 ml-10">{review.comment}</p>
                      {/* Extended WiFi details shown below comment */}
                      {review.wifi_speed_mbps && (
                        <div className="ml-10 mt-2 flex items-center gap-3 text-xs text-gray-400">
                          <span className="flex items-center gap-1">
                            <Wifi className="w-3 h-3 text-teal-500" />
                            ↓ {review.wifi_speed_mbps} Mbps
                          </span>
                          {review.wifi_upload_mbps && (
                            <span>↑ {review.wifi_upload_mbps} Mbps</span>
                          )}
                          {review.wifi_latency_ms && (
                            <span>{review.wifi_latency_ms}ms ping</span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </motion.div>
              
              {/* Best Times Calendar - After Reviews */}
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}>
                <BestTimesCalendar 
                  bestTimes={venue.best_times}
                  openingHours={venue.opening_hours}
                  isPremium={venue.is_premium}
                />
              </motion.div>
            </div>

            {/* Right Sidebar */}
            <div className="space-y-4">
              {/* Pricing */}
              <motion.div initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} className="bg-white rounded-xl p-5 border border-gray-100">
                <h3 className="font-semibold text-gray-900 mb-3">Price Level</h3>
                <div className="space-y-3">
                  {(venue.price_range.coffee_avg > 0 || true) && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 flex items-center gap-2">
                        <Coffee className="w-4 h-4 text-amber-600" /> Coffee
                      </span>
                      <PriceDisplay value={venue.price_range.coffee_avg} size="md" />
                    </div>
                  )}
                  {(venue.price_range.food_avg > 0 || true) && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 flex items-center gap-2">
                        <UtensilsCrossed className="w-4 h-4 text-orange-600" /> Food
                      </span>
                      <PriceDisplay value={venue.price_range.food_avg} size="md" />
                    </div>
                  )}
                </div>
              </motion.div>

              {/* Contact */}
              <motion.div initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }} className="bg-white rounded-xl p-5 border border-gray-100">
                <h3 className="font-semibold text-gray-900 mb-3">Details</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex items-start gap-2">
                    <MapPin 
                      className="w-4 h-4 mt-0.5" 
                      style={hasCustomBranding ? { color: brandStyles.primary } : { color: '#008B8B' }}
                    />
                    <div><p className="font-medium">{venue.location.address}</p><p className="text-gray-500">{venue.location.city} {venue.location.postcode}</p></div>
                  </div>
                  {venue.opening_hours && (
                    <div className="flex items-start gap-2">
                      <Clock 
                        className="w-4 h-4 mt-0.5" 
                        style={hasCustomBranding ? { color: brandStyles.primary } : { color: '#008B8B' }}
                      />
                      <div><p className="font-medium">Hours</p><p className="text-gray-500">{venue.opening_hours}</p></div>
                    </div>
                  )}
                  {/* Website only shown for premium listings */}
                  {venue.is_premium && venue.website && (
                    <a 
                      href={venue.website} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="flex items-center gap-2"
                      style={hasCustomBranding ? { color: brandStyles.primary } : { color: '#008B8B' }}
                      onClick={() => {
                        // Track website click
                        axios.post(`${API}/track/website-click`, {
                          venue_id: venue.id,
                          venue_name: venue.name,
                          website_url: venue.website
                        }).catch(() => {});
                      }}
                      data-testid="venue-website-link"
                    >
                      <Globe className="w-4 h-4" /> Website
                    </a>
                  )}
                  {/* Social Media Links - Premium only */}
                  {venue.is_premium && venue.contact_details && (
                    <div className="flex items-center gap-3">
                      {venue.contact_details.social_facebook && (
                        <a 
                          href={venue.contact_details.social_facebook} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="p-1.5 rounded-lg hover:bg-blue-50 transition-colors"
                          title="Facebook"
                          data-testid="venue-facebook-link"
                        >
                          <Facebook className="w-4 h-4 text-blue-600" />
                        </a>
                      )}
                      {venue.contact_details.social_instagram && (
                        <a 
                          href={venue.contact_details.social_instagram} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="p-1.5 rounded-lg hover:bg-pink-50 transition-colors"
                          title="Instagram"
                          data-testid="venue-instagram-link"
                        >
                          <Instagram className="w-4 h-4 text-pink-600" />
                        </a>
                      )}
                      {venue.contact_details.social_twitter && (
                        <a 
                          href={venue.contact_details.social_twitter} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="p-1.5 rounded-lg hover:bg-sky-50 transition-colors"
                          title="X/Twitter"
                          data-testid="venue-twitter-link"
                        >
                          <Twitter className="w-4 h-4 text-sky-500" />
                        </a>
                      )}
                    </div>
                  )}
                  {/* Laptop Friendly badge for premium */}
                  {venue.is_premium && (
                    <div 
                      className="flex items-center gap-2 text-sm font-medium"
                      style={hasCustomBranding ? { color: brandStyles.primary } : { color: '#16a34a' }}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      Laptop Friendly
                    </div>
                  )}
                </div>
              </motion.div>

              {/* Map */}
              <motion.div initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }} className="h-40 rounded-xl overflow-hidden border border-gray-200">
                <VenueMap venues={[venue]} center={[venue.location.lat, venue.location.lng]} zoom={15} interactive={false} />
              </motion.div>

              {/* Booking Links (OpenTable, TheFork, etc.) - Premium Only */}
              <motion.div initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.25 }}>
                <BookingLinks 
                  booking={venue.booking} 
                  externalBooking={venue.external_booking} 
                  website={venue.website}
                  isPremium={venue.is_premium}
                />
              </motion.div>

              {/* Ad Banner */}
              <AdBanner adSlot="" adFormat="vertical" className="rounded-xl overflow-hidden" />

              {/* Claim CTA - Prominent teal box */}
              {!venue.is_claimed && !venue.is_premium && (
                <button 
                  onClick={() => navigate(`/claim?venue=${venue.id}`)} 
                  className="w-full p-4 bg-teal-600 hover:bg-teal-700 rounded-xl text-center transition-colors shadow-sm"
                  data-testid="claim-venue-btn"
                >
                  <p className="text-white font-semibold text-base mb-1">Own this venue?</p>
                  <p className="text-teal-100 text-sm">Claim it to manage your listing →</p>
                </button>
              )}
            </div>
          </div>
        </div>
        </div>
        </div>
        
        {/* Ezoic Ad - Before footer */}
        <div className="max-w-7xl mx-auto px-4 py-6">
          <EzoicAd placementId={105} />
        </div>
      </div>
      <Footer />

      {/* Report Error Modal */}
      <AnimatePresence>
        {showReportModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-[9999]"
              onClick={() => setShowReportModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-0 z-[9999] flex items-center justify-center p-4 overflow-y-auto"
            >
              <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto my-auto">
              <div className="p-5 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-orange-500" />
                    <h3 className="font-semibold text-gray-900">Report an Error</h3>
                  </div>
                  <button onClick={() => setShowReportModal(false)} className="p-1 hover:bg-gray-100 rounded-lg">
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
                <p className="text-sm text-gray-500 mt-1">Help us keep information accurate for {venue?.name}</p>
              </div>
              
              <form onSubmit={handleReportError} className="p-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">What&apos;s wrong?</label>
                  <div className="grid grid-cols-2 gap-2">
                    {ERROR_TYPES.map((type) => (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => setReportForm({ ...reportForm, error_type: type.value })}
                        className={`text-left px-3 py-2 rounded-lg text-sm border transition-all ${
                          reportForm.error_type === type.value
                            ? "bg-orange-50 border-orange-300 text-orange-700"
                            : "bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100"
                        }`}
                      >
                        {type.label}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Describe the issue</label>
                  <textarea
                    value={reportForm.message}
                    onChange={(e) => setReportForm({ ...reportForm, message: e.target.value })}
                    placeholder="Please provide details about what's incorrect..."
                    rows={3}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:ring-2 focus:ring-orange-200 focus:border-orange-300"
                    data-testid="report-message-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Your email (optional)</label>
                  <input
                    type="email"
                    value={reportForm.reporter_email}
                    onChange={(e) => setReportForm({ ...reportForm, reporter_email: e.target.value })}
                    placeholder="email@example.com"
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:ring-2 focus:ring-orange-200 focus:border-orange-300"
                    data-testid="report-email-input"
                  />
                  <p className="text-xs text-gray-400 mt-1">We&apos;ll only contact you if we need more information</p>
                </div>
                
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowReportModal(false)}
                    className="flex-1 px-4 py-2.5 rounded-lg border border-gray-200 text-gray-700 font-medium hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={reportSubmitting || !reportForm.error_type || !reportForm.message.trim()}
                    className="flex-1 px-4 py-2.5 rounded-lg bg-orange-500 text-white font-medium hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    data-testid="submit-report-button"
                  >
                    {reportSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" /> Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" /> Submit Report
                      </>
                    )}
                  </button>
                </div>
              </form>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Workspace Booking Modal */}
      <AnimatePresence>
        {showBookingModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50"
              onClick={() => !bookingSuccess && setShowBookingModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
            >
              <div className="w-full max-w-md bg-white rounded-xl shadow-xl max-h-[90vh] overflow-y-auto my-auto">
              {bookingSuccess ? (
                <div className="p-6 text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Booking Confirmed!</h3>
                  <p className="text-gray-600 mb-4">
                    Your workspace at <strong>{venue?.name}</strong> has been booked for <strong>{selectedDate}</strong>.
                  </p>
                  <p className="text-sm text-gray-500 mb-6">A confirmation email has been sent to {bookingForm.customer_email}</p>
                  <button
                    onClick={() => {
                      setShowBookingModal(false);
                      setBookingSuccess(false);
                      setBookingForm({ time_slot: "morning", spaces: 1, customer_name: "", customer_email: "", customer_phone: "", notes: "" });
                    }}
                    className="btn-primary"
                  >
                    Done
                  </button>
                </div>
              ) : (
                <>
                  <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900">Book Workspace</h3>
                    <button onClick={() => setShowBookingModal(false)} className="p-1 hover:bg-gray-100 rounded">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <form onSubmit={handleBookWorkspace} className="p-4 space-y-4">
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <p className="text-sm font-medium text-blue-900">{venue?.name}</p>
                      <p className="text-xs text-blue-700">
                        {selectedDate} • {availability?.slots?.[bookingForm.time_slot]?.label || bookingForm.time_slot}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Number of Spaces</label>
                      <select
                        value={bookingForm.spaces}
                        onChange={(e) => setBookingForm({ ...bookingForm, spaces: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                        data-testid="booking-spaces"
                      >
                        {[...Array(Math.min(5, availability?.slots?.[bookingForm.time_slot]?.available_spaces || 1))].map((_, i) => (
                          <option key={i + 1} value={i + 1}>{i + 1} space{i > 0 ? 's' : ''}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Your Name *</label>
                      <input
                        type="text"
                        value={bookingForm.customer_name}
                        onChange={(e) => setBookingForm({ ...bookingForm, customer_name: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                        placeholder="John Doe"
                        required
                        data-testid="booking-name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                      <input
                        type="email"
                        value={bookingForm.customer_email}
                        onChange={(e) => setBookingForm({ ...bookingForm, customer_email: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                        placeholder="john@example.com"
                        required
                        data-testid="booking-email"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone (optional)</label>
                      <input
                        type="tel"
                        value={bookingForm.customer_phone}
                        onChange={(e) => setBookingForm({ ...bookingForm, customer_phone: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                        placeholder="+44 7700 900000"
                        data-testid="booking-phone"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
                      <textarea
                        value={bookingForm.notes}
                        onChange={(e) => setBookingForm({ ...bookingForm, notes: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm h-16 resize-none"
                        placeholder="Any special requirements..."
                        data-testid="booking-notes"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={bookingSubmitting}
                      className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                      data-testid="confirm-booking"
                    >
                      {bookingSubmitting ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Booking...</>
                      ) : (
                        <><CheckCircle className="w-4 h-4" /> Confirm Booking</>
                      )}
                    </button>
                  </form>
                </>
              )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      
      {/* Contributor Auth Modal - shown after review submission or when clicking Email sign-in */}
      <ContributorAuthModal
        isOpen={showAuthModal}
        onClose={() => {
          setShowAuthModal(false);
          setPendingReviewId(null);
        }}
        onSuccess={(contributor) => {
          setShowAuthModal(false);
          setPendingReviewId(null);
          // Update contributor session state and pre-fill name
          if (contributor) {
            setContributorSession(contributor);
            setReviewForm(prev => ({ ...prev, user_name: contributor.display_name || contributor.email?.split('@')[0] || '' }));
          }
        }}
        reviewId={pendingReviewId}
        mode={pendingReviewId ? "post-review" : "login"}
      />
    </div>
  );
}
